/**
 * TroiRefinePanel — chat, demoted. Troi is no longer the face of the Shift
 * Planner; she is a slide-in refinement channel (right on desktop, bottom sheet
 * on mobile) opened from "Troi fragen". Natural-language requests stream and
 * fold onto the SAME board via the existing stream → invalidate → refold path;
 * the chat is a side channel, not the record.
 *
 * Rendering: Troi answers on the 202-async-turn path (supports_streaming:false),
 * so her reply is NOT in `streaming.content` — it lands in the session message
 * cache as `turn.delta`/`turn.completed` frames handled by useSessionEventStream.
 * We therefore render the SESSION TRANSCRIPT (the same MessageList the main chat
 * uses), not the transient streaming state. Reading `streaming.content` here was
 * the "Troi gives no answer" bug: a plain-prose reply (an explanation, a
 * warning, a refusal) went to the cache and rendered nowhere.
 *
 * The transcript is filtered to real conversation: user turns, Troi's prose
 * replies, and the in-flight "thinking" placeholder. A message with no prose
 * would render as an empty bubble, so it is dropped here.
 *
 * Board-first agent → no chat chips (house rule). Terracotta is Troi's IDENTITY
 * hue only (the header glyph), never an action; Action Blue stays the sole
 * interactive accent.
 */
import { useTranslation } from 'react-i18next'

import type { SessionDetail } from '@/hooks/useSessions'
import { cn } from '@/lib/utils'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { BoardAgentChat, ChatInput } from '@/components/chat'
import { useStreamingMessage } from '@/hooks/useStreamingChat'
import { useIsDesktop } from './useIsDesktop'

export function TroiRefinePanel({
  open,
  onOpenChange,
  session,
  streaming,
  onSend,
  sendPending,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Absent before the first send mints the session (the composer below is
   *  the mint — useDeferredFirstSend in the controller). */
  session?: SessionDetail
  streaming: ReturnType<typeof useStreamingMessage>
  onSend: (content: string) => void
  sendPending: boolean
}) {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isDesktop ? 'right' : 'bottom'}
        className={cn(
          'flex flex-col gap-0 border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] p-0',
          isDesktop ? 'h-full w-[min(92vw,26rem)] sm:max-w-md' : 'h-[85vh] rounded-t-[18px]',
        )}
      >
        <SheetHeader className="shrink-0 space-y-0 border-b border-[var(--color-surface-border)] px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] text-left">
          <SheetTitle className="flex items-center gap-2 text-[17px] font-semibold tracking-[-0.01em] text-[var(--color-surface-ink)]">
            <span
              aria-hidden="true"
              className="flex h-7 w-7 items-center justify-center rounded-full text-[13px] font-semibold text-[var(--color-neutral-0)]"
              style={{ backgroundColor: 'var(--color-terracotta-500)' }}
            >
              T
            </span>
            {t('roster.troi.title', 'Ask Troi')}
          </SheetTitle>
        </SheetHeader>

        {session ? (
          <BoardAgentChat
            session={session}
            streaming={streaming}
            className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
            emptyMessage={t(
              'roster.troi.hint',
              'Describe a change in plain words — e.g. "give Anna the weekend off". Troi applies it to the board.',
            )}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <p className="text-[14px] text-[var(--color-surface-mute)]">
              {t(
                'roster.troi.hint',
                'Describe a change in plain words — e.g. "give Anna the weekend off". Troi applies it to the board.',
              )}
            </p>
          </div>
        )}

        <div className="shrink-0 border-t border-[var(--color-surface-border)] px-4 py-3 pb-safe">
          <ChatInput
            onSend={onSend}
            isPending={sendPending}
            placeholder={t('roster.troi.placeholder', 'Ask Troi to adjust the roster…')}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
